import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, createTheme } from '@mui/material/styles';
import screens from './screens';
import themeV1Colors from './themeColors';
import type from './typography';

const { fontWeight, variants: typography } = type;

const breakpoints = {
  values: {
    xs: 0,
    ...screens,
  },
};

/**
 * A lot the theme values are powered by CSS variables. However there is
 * a good portion of the site that is still using the V1 color values powered
 * by MUI. Due to V2 being an "opt-in" experience (with some exceptions;
 * MuiButtons are V2 by default) we are going to keep using the V1 colors for
 * the palette.
 *
 * Updating the palette is a task unto itself that requires updating a lot of
 * components.
 */
const subTheme = createTheme({
  breakpoints,
  palette: {
    destructive: themeV1Colors.destructive,
    primary: themeV1Colors.primary,
    secondary: themeV1Colors.secondary,
    tertiary: themeV1Colors.tertiary,
    grey: {
      300: themeV1Colors.grey300,
      500: themeV1Colors.grey500,
      light: themeV1Colors.lightGrey,
      dark: themeV1Colors.darkGrey,
      transparent: themeV1Colors.transparentGrey,
      ...themeV1Colors.grey,
    },
    green: {
      light: themeV1Colors.lightGreen,
    },
    text: themeV1Colors.text,
    error: {
      ...themeV1Colors.error,
    },
    success: {
      ...themeV1Colors.success,
    },
    warning: {
      main: '#FFD464',
      light: '#FFF0BD',
    },
    background: {
      light: 'rgba(243, 245, 245, 0.6)',
      offWhite: themeV1Colors.offWhite,
      superAdmin: themeV1Colors.adminColor,
      white: themeV1Colors.white,
    },
    donorAttributes: {
      boardMember: themeV1Colors.boardMember,
      majorDonor: themeV1Colors.majorDonor,
      recurring: themeV1Colors.recurring,
      volunteer: themeV1Colors.volunteer,
      member: themeV1Colors.member,
    },
    progressBar: {
      darkGrey: themeV1Colors.progressDarkGrey,
      lightGrey: themeV1Colors.progressLightGrey,
    },
    primaryLink: themeV1Colors.tertiary,
    primaryButton: themeV1Colors.primaryButton,
    secondaryButton: themeV1Colors.secondaryButton,
  },
  shape: {
    borderRadius: 8,
  },
  /* Typography as per styleguide */
  typography: {
    color: 'rgb(var(--color-black))',
    fontFamily: ['Poppins', 'Roboto'].join(','),
    fontWeightMedium: fontWeight.medium,
    fontWeightRegular: fontWeight.regular,
    fontWeightSemiBold: fontWeight.semiBold,
    h1: typography.h1,
    h2: typography.h2,
    h3: typography.h3,
    h4: typography.h4,
    // h5: {},
    // h6: {},
    // subtitle1: {},
    // subtitle2: {},
    body1: typography.body1,
    body2: typography.body2,
    body3: typography.body3,
    body4: typography.body4,
    // button: {},
    caption1: typography.caption1,
    caption2: typography.caption2,
    // overline: {},
  },
});

const theme = createTheme(subTheme, {
  components: {
    MuiAlert: {
      styleOverrides: {
        root: {
          padding: '4px 12px',
        },
        message: {
          ...typography.body4,
          '& a': typography.body4,
        },
        standardSuccess: {
          color: 'rgb(var(--color-success-contrastText))',
          backgroundColor: 'rgb(var(--color-success-light))',
        },
        standardError: {
          color: 'rgb(var(--color-error-contrastText))',
          backgroundColor: 'rgb(var(--color-error-main))',
          '& $icon': {
            color: 'rgb(var(--color-error-contrastText))',
          },
        },
        standardWarning: {
          backgroundColor: 'rgb(var(--color-warning-light))',
          color: 'rgb(var(--color-black))',
        },
        outlinedWarning: {
          backgroundColor: 'rgb(var(--color-warning-light))',
          borderColor: 'rgb(var(--color-warning-main))',
          color: 'rgb(var(--color-black))',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        hasPopupIcon: {
          '.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            paddingRight: '30px !important',
          },
        },
        inputRoot: {
          padding: '5px',
        },
        input: {
          padding: '5px',
        },
        listbox: {
          padding: 0,
        },
        option: {
          '&.Mui-focused': {
            backgroundColor: `rgb(var(--color-grey-100)) !important`,
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        color: 'primary',
        'data-theme': 'v2',
      },
      styleOverrides: {
        containedError: {
          '&:hover': {
            backgroundColor: 'rgb(var(--color-error-light))',
          },
        },
        root: {
          borderRadius: '8px',
          fontSize: typography.body1.fontSize,
          fontWeight: fontWeight.medium,
          lineHeight: 1.5,
          letterSpacing: '0.15px',
          textTransform: 'capitalize',
          [subTheme.breakpoints.down('sm')]: {
            minWidth: 'auto',
          },
          '&.Mui-disabled': {
            cursor: 'not-allowed',
            pointerEvents: 'auto',
            backgroundColor: 'rgb(var(--color-grey-100))',
            color: 'rgb(var(--color-text-secondary))',
          },
          '&:hover&.Mui-disabled': {
            color: 'rgb(var(--color-text-secondary))',
          },
          path: {
            fill: 'currentColor !important',
          },
        },
        contained: {
          boxShadow: 'none',
          '& > *': {
            fontWeight: fontWeight.medium,
          },
          '&.Mui-disabled': {
            color: 'rgb(var(--color-grey-900))',
            backgroundColor: 'rgb(var(--color-grey-100))',
          },
          '&:hover': {
            boxShadow: 'none',
          },
          '&.Mui-disabled:hover': {
            backgroundColor: 'rgb(var(--color-grey-100))',
          },
        },
        containedPrimary: {
          backgroundColor: 'rgb(var(--color-primary-main))',
          color: 'rgb(var(--color-primary-contrastText))',
          '& path': {
            fill: 'rgb(var(--color-primary-contrastText))',
          },
          '&:hover': {
            backgroundColor: 'rgb(var(--color-primary-light))',
          },
          '&:focus': {
            backgroundColor: 'rgb(var(--color-primary-light))',
          },
          '&:active': {
            backgroundColor: 'rgb(var(--color-primary-dark))',
          },
        },
        containedSecondary: {
          backgroundColor: 'rgb(var(--color-secondary-main))',
          color: 'rgb(var(--color-secondary-contrastText))',
          '& path': {
            fill: 'rgb(var(--color-secondary-contrastText))',
          },
          '&:hover': {
            backgroundColor: 'rgb(var(--color-secondary-light))',
          },
          '&:focus': {
            backgroundColor: 'rgb(var(--color-secondary-light))',
          },
          '&:active': {
            backgroundColor: 'rgb(var(--color-secondary-dark))',
          },
        },
        containedSizeSmall: {
          padding: '10px 16px',
        },
        containedSizeMedium: {
          padding: '12px 24px',
        },
        containedSizeLarge: {
          padding: '16px 32px',
        },
        textSizeSmall: {
          padding: '10px 16px',
        },
        textSizeMedium: {
          padding: '12px 24px',
        },
        textSizeLarge: {
          padding: '16px 32px',
        },
        text: {
          padding: '18px 12px',
          '&:disabled': {
            color: 'rgb(var(--color-grey-900))',
            backgroundColor: 'transparent',
            '& path': {
              fill: 'rgb(var(--color-grey-900))',
            },
          },
          '&.Mui-disabled:hover': {
            backgroundColor: 'transparent',
          },
        },
        textPrimary: {
          color: 'rgb(var(--color-tertiary-main))',
          '& path': {
            fill: 'rgb(var(--color-tertiary-main))',
          },
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'rgb(var(--color-tertiary-secondary))',
            '& path': {
              fill: 'rgb(var(--color-tertiary-secondary))',
            },
          },
          '&:focus': {
            color: 'rgb(var(--color-tertiary-secondary))',
            '& path': {
              fill: 'rgb(var(--color-tertiary-secondary))',
            },
          },
          '&:active': {
            color: 'rgb(var(--color-tertiary-dark))',
            '& path': {
              fill: 'rgb(var(--color-tertiary-dark))',
            },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: 'rgb(var(--color-grey-600))',
          },
        },
      },
    },
    MuiCollapse: {
      defaultProps: {
        // <Collapse> uses's min height and visibility to hide collapsed content.
        // However, we need display: none when they are hidden, or else the grid
        // styles on the parent will add gaps between each collapsed element. This
        // will result in a bunch of space between hidden divs.
        classes: {
          hidden: 'hidden',
        },
      },
    },
    MuiChip: {
      defaultProps: {
        deleteIcon: <CloseIcon />,
      },
      styleOverrides: {
        root: {
          height: 'auto',
        },
        colorDefault: {
          background: 'rgb(var(--color-grey-100))',
        },
        deleteIcon: {
          color: 'rgb(var(--color-black))',
          marginRight: 0,
          width: '16px',
          height: '16px',
          marginLeft: '6px',
        },
        sizeSmall: {
          padding: '4px 8px',
          borderRadius: '8px',
        },
        sizeMedium: {
          padding: '8px',
          borderRadius: '8px',
        },
        label: {
          padding: 0,
          fontSize: '12px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '24px',
          [subTheme.breakpoints.down('sm')]: {
            padding: '16px',
          },
        },
        spacing: {
          marginLeft: '8px',
          [subTheme.breakpoints.down('sm')]: {
            marginLeft: '0',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '24px',
        },
        dividers: {
          padding: '24px',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          flexShrink: 1,
          padding: '24px',
          [subTheme.breakpoints.down('sm')]: {
            padding: '16px',
          },
        },
      },
    },
    MuiDisabled: {
      styleOverrides: {
        cursor: 'not-allowed',
      },
    },
    MuiDivider: {
      styleOverrides: {
        light: {
          borderColor: themeV1Colors.transparentWhite,
        },
        vertical: {
          borderColor: 'rgb(var(--color-black))',
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: `rgb(var(--color-grey-100)) !important`,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          transition: 'none',

          '&:hover': {
            backgroundColor: `rgb(var(--color-grey-100)) !important`,
          },
          '&.Mui-focused': {
            backgroundColor: `rgb(var(--color-grey-100)) !important`,
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '&:Mui-disabled': {
            cursor: 'not-allowed',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        contained: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          letterSpacing: '0.15px',
        },
        input: {
          lineHeight: 1.5,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: '16px',
          borderRadius: '8px',
          zIndex: 1,
        },
        bar: {
          borderRadius: '8px',
        },
        // background styles
        dashed: {
          animation: 'none',
          backgroundImage: 'none !important',
        },
        dashedColorPrimary: {
          backgroundColor: themeV1Colors.lightGrey,
        },
        dashedColorSecondary: {
          backgroundColor: 'rgb(var(--color-grey-300))',
        },
        // current progress styles
        barColorPrimary: {
          backgroundColor: 'rgb(var(--color-primary-main))',
        },
        barColorSecondary: {
          backgroundColor: 'rgb(var(--color-grey-900))',
        },
        // expected progress styles
        colorPrimary: {
          backgroundColor: 'rgb(var(--color-grey-600))',
        },
        colorSecondary: {
          backgroundColor: 'rgb(var(--color-grey-600))',
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'none',
      },
      styleOverrides: {
        root: {
          ...typography.body1,
          color: 'rgb(var(--color-tertiary-main))',
          cursor: 'pointer',
          textTransform: 'initial',
          '&:hover': {
            color: 'rgb(var(--color-tertiary-light))',
          },
          '&:focus': {
            color: 'rgb(var(--color-tertiary-light))',
          },
          '&:active': {
            color: 'rgb(var(--color-tertiary-dark))',
          },
          '&:disabled': {
            color: 'rgb(var(--color-grey-900))',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(themeV1Colors.black, 0.04),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(themeV1Colors.black, 0.08),

            '&:hover': {
              backgroundColor: alpha(themeV1Colors.black, 0.08),
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          transform: 'translate(14px, 12px) scale(1)',
          '&.Mui-required span': {
            display: 'none',
          },
          '&.Mui-required::after': {
            content: '""',
            width: '6px',
            height: '6px',
            background: 'rgb(var(--color-error-main))',
            display: 'inline-block',
            marginBottom: '2px',
            marginLeft: '6px',
            borderRadius: '50%',
          },
        },
        shrink: {
          transform: 'translate(14px, -9px) scale(0.75)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: 'rgb(var(--color-white))',
        },
        input: {
          padding: '12.5px 12px',

          '&.Mui-disabled': {
            cursor: 'not-allowed !important',
          },
        },
        inputMultiline: {
          padding: 0,
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        rounded: {
          color: 'rgb(var(--color-grey-900))',
          '&:not(.MuiPaginationItem-ellipsis)': {
            '&:hover': {
              color: 'rgb(var(--color-text-primary))',
              backgroundColor: 'rgb(var(--color-grey-600))',
            },
            '&:focus': {
              color: 'rgb(var(--color-text-primary))',
              backgroundColor: 'rgb(var(--color-grey-600))',
            },
          },
          '&.Mui-selected': {
            color: 'rgb(var(--color-white))',
            backgroundColor: 'rgb(var(--color-secondary-main))',
            '&:hover': {
              color: 'rgb(var(--color-white))',
              backgroundColor: 'rgb(var(--color-secondary-main))',
            },
            '&:focus': {
              color: 'rgb(var(--color-white))',
              backgroundColor: 'rgb(var(--color-secondary-main))',
            },
          },
          '&.MuiPaginationItem-previousNext': {
            color: 'rgb(var(--color-text-primary))',

            '&:Mui-disabled': {
              opacity: 1,
              color: 'rgb(var(--color-grey-600))',
            },
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: 'rgb(var(--color-tertiary-main))',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        },
      },
      styleOverrides: {
        select: {
          padding: '11.5px 12px',
        },
        standard: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '&.Mui-active': {
            boxShadow: 'none',
          },
        },
        colorPrimary: {
          thumb: {
            backgroundColor: 'red',
          },
        },
        mark: {
          height: 16,
          marginTop: -7,
        },
        markActive: {
          height: 16,
          marginTop: -7,
          backgroundColor: 'currentColor',
        },
        rail: {
          height: 4,
          marginTop: -1,
        },
        track: {
          height: 4,
          marginTop: -1,
        },
        thumb: {
          height: 32,
          width: 32,
          marginLeft: -15,
          marginTop: -15,
          '&:hover': {
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            height: 32,
            width: 32,
            marginLeft: -15,
            marginTop: -15,
          },
        },
        thumbColorPrimary: {
          '&:hover': {
            boxShadow: 'none',
          },
          '&.Mui-focusVisible': {
            boxShadow: 'none',
          },
        },
        thumbColorSecondary: {
          '&:hover': {
            boxShadow: 'none',
          },
          '&.Mui-focusVisible': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: 'rgb(var(--color-grey-600))',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          ...typography.h4,
          textTransform: 'none',
          padding: '12px 28px',
        },
        textColorPrimary: { color: 'rgb(var(--color-grey-900))' },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${themeV1Colors.transparentGrey}`,
        },
        indicator: {
          borderBottom: `2px solid rgb(var(--color-subNav-active))`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          background: 'inherit',
          border: 'none',
        },
        head: {
          boxShadow: `0 1px 0 0 ${themeV1Colors.grey[300]}`,
          fontWeight: fontWeight.medium,
          padding: '0.75rem 1rem',
          '&:first-of-type': {
            borderRadius: '0.5rem 0 0 0.5rem',
          },
          '&:last-of-type': {
            borderRadius: '0 0.5rem 0.5rem 0',
          },
        },
        body: {
          fontWeight: fontWeight.regular,
          padding: '0.75rem 1rem',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: themeV1Colors.grey[100],
          position: 'sticky',
          top: 0,
          zIndex: 20,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: themeV1Colors.white,
          borderColor: themeV1Colors.grey[300],
          borderWidth: '0 0 1px',
          '&.MuiTableRow-hover:hover': {
            backgroundColor: themeV1Colors.grey[50],
          },
        },
        head: {
          backgroundColor: themeV1Colors.grey[100],
          border: 'none',
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        placement: 'top',
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgb(var(--color-black))',
          borderRadius: '4px',
          fontSize: '12px',
          '&.MuiTooltip-light': {
            backgroundColor: 'rgb(var(--color-white))',
            boxShadow: `0 4px 12px 0 rgba(0, 0, 0, 0.25)`,
            color: 'rgb(var(--color-black))',
          },
        },
        arrow: {
          color: 'rgb(var(--color-black))',
          '.MuiTooltip-light &': {
            color: 'rgb(var(--color-white))',
          },
        },
        tooltipPlacementBottom: {
          margin: '6px 0',
        },
      },
    },
  },
});

export default theme;
